import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Badge, Button, Card, EmptyState, Field, HeroPill, Input, MobileHero, MobilePage, SectionHeader, Select, Skeleton } from '../../components/mobile/MobilePageKit';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const CLIENT_URL = process.env.REACT_APP_CLIENT_URL || 'https://psych-client.onrender.com';

const MSE_TEST_NAME = 'Mental State Examination';

const TEST_CATEGORY_MAP = {
  disorders: {
    name: 'Assessment of Specific Disorders',
    testNames: [
      'Patient Health Questionnaire - 9 Items',
      'Depression Anxiety Stress Scales - 21 Items',
      'Depression Anxiety Stress Scale (21-item)',
      'Edinburgh Postnatal Depression Scale',
      'Generalized Anxiety Disorder - 7 Items',
      'Prolonged Grief Disorder Scale - Revised',
      'PTSD Checklist for DSM-5',
      'Impact of Event Scale – Revised',
      'Life Events Checklist for DSM-5',
      'Adverse Childhood Experiences Questionnaire',
      'PTSD Checklist – Civilian Version',
      'PTSD Checklist – Military Version',
      'Primary Care PTSD Screen for DSM-5',
      'Fast Alcohol Screening Test',
      'Alcohol Use Disorders Identification Test',
      'CAGE Substance Abuse Screening Tool',
      'Clinical Institute Withdrawal Assessment for Alcohol – Revised',
      'Clinical Opiate Withdrawal Scale',
      'Michigan Alcohol Screening Test',
      'Autism Spectrum Quotient',
      'Adult ADHD Self-Report Scale (WHO ASRS-v1.1)',
      'Brief Psychiatric Rating Scale',
      'Strengths and Difficulties Questionnaire',
      'Vanderbilt ADHD Diagnostic Rating Scale (parent-report)',
      'Pediatric Symptom Checklist — 17',
      'Student Risk Screening Scale — Internalizing and Externalizing',
      'Beck Depression Inventory — Second Edition',
      'Patient Health Questionnaire — Adolescent Version',
      "Children's Depression Inventory 2 (Self-Report Full Length)",
      "Revised Children's Anxiety and Depression Scale (47-item Child Self-Report)",
      'Screen for Child Anxiety Related Emotional Disorders (Child Self-Report)',
      'Systematic Screening for Behavior Disorders',
      'Child PTSD Symptom Scale',
      'Child and Adolescent Trauma Screen',
      'Traumatic Events Screening Inventory for Children',
      'CRAFFT Adolescent Substance Use Screen (CRAFFT 2.1)',
      'Revised Child Anxiety and Depression Scale – 25 Items (RCADS-25)',
    ],
  },
  cbt: {
    name: 'CBT-Informed Assessments',
    testNames: [
      'African-Adapted Cognitive Distortions Scale',
      'Automatic Thoughts Questionnaire',
      'Maladaptive Schema Scale - Young Schema Questionnaire Aligned',
      'Cognitive Emotion Regulation Questionnaire',
    ],
  },
  personality: {
    name: 'Personality Assessment',
    testNames: [
      'Five Factor Personality Scale - 30 Items',
      'Personality Inventory for DSM-5 - Short Form',
      'IPIP Big Five Factor Markers — 50-Item',
      'Mini-IPIP — 20-Item Big Five',
      'IPIP-NEO-120',
      'IPIP-HEXACO Personality Inventory',
      'IPIP-BIS/BAS (Behavioral Inhibition/Activation Systems)',
    ],
  },
  wellbeing: {
    name: 'Well-Being & Self-Esteem',
    testNames: [
      'Rosenberg Self-Esteem Scale',
      'Coopersmith Self-Esteem Inventory',
      'Warwick-Edinburgh Mental Well-Being Scale',
      'Satisfaction With Life Scale',
      'Positive and Negative Affect Schedule',
      'State Self-Esteem Scale',
      'YSR/CBCL Competence Scales',
      'Self-Perception Profile for Children',
      "Piers-Harris Children's Self-Concept Scale",
      'PROMIS Anger Short Form 5a',
      'WHO-5 Well-Being Index',
      'Work and Social Adjustment Scale',
      'Sheehan Disability Scale',
      'Global Assessment of Functioning',
      'Schutte Self-Report Emotional Intelligence Test',
      'Wong and Law Emotional Intelligence Scale',
      'Trait Emotional Intelligence Questionnaire — Short Form',
      'Student Life Satisfaction Scale',
    ],
  },
  social: {
    name: 'Social Support & Loneliness',
    testNames: [
      'UCLA Loneliness Scale — Version 3',
      'Multidimensional Scale of Perceived Social Support',
      'De Jong Gierveld Loneliness Scale',
      "Children's Loneliness Scale",
    ],
  },
  resilience: {
    name: 'Resilience & Coping',
    testNames: [
      'Resilience Scale for Adolescents',
      'Brief Resilience Scale',
      'Connor-Davidson Resilience Scale',
      'Child and Youth Resilience Measure',
      'Adolescent Coping Scale',
      'COPE Inventory',
    ],
  },
  mood: {
    name: 'Mood & Emotional Assessment',
    testNames: [
      "Children's Hope Scale",
      'Mood and Feelings Questionnaire',
      'Mood Disorder Questionnaire',
      'Altman Self-Rating Mania Scale',
      'Young Mania Rating Scale',
      'Bipolar Spectrum Diagnostic Scale',
      'Rapid Mood Screener',
      'Montgomery-Åsberg Depression Rating Scale',
      'Hamilton Rating Scale for Depression (HDRS-17)',
      'Functional Assessment Short Test — Bipolar Disorder',
      'Difficulties in Emotion Regulation Scale (DERS)',
    ],
  },
  development: {
    name: 'Identity & Developmental',
    testNames: [
      'Ego Identity Process Questionnaire',
      'Erikson Psychosocial Stage Inventory',
      'Objective Measure of Ego Identity Status',
      'Empathy Quotient',
      'Chalder Fatigue Scale (CFQ-11)',
      'Self-Perception Profile for Young People',
    ],
  },
  other: {
    name: 'Other Available Tools',
    testNames: [
      'Columbia Suicide Severity Rating Scale',
      'Transgression-Related Interpersonal Motivations Inventory (TRIM-18)',
      'Heartland Forgiveness Scale',
      'Experiences in Close Relationships – Revised',
      'Child-Parent Relationship Scale',
      'Maslach Burnout Inventory (MBI)',
      'Utrecht Work Engagement Scale – 17 (UWES-17)',
      'General Practitioner Assessment of Cognition',
      'Neuropsychiatric Inventory Questionnaire (NPI-Q)',
    ],
  },
};

const categorizeTests = (tests) => {
  // MSE is handled separately — exclude from all category maps
  const mseTest = tests.find(t => t.name === MSE_TEST_NAME);

  const categoryEntries = Object.entries(TEST_CATEGORY_MAP).map(([catId, catInfo]) => ({
    id: catId,
    name: catInfo.name,
    tests: tests.filter(
      t => t.name !== MSE_TEST_NAME && catInfo.testNames.includes(t.name)
    ),
  }));

  const categorizedIds = new Set([
    ...(mseTest ? [mseTest.id] : []),
    ...categoryEntries.flatMap(c => c.tests.map(t => t.id)),
  ]);

  const uncategorized = tests.filter(t => !categorizedIds.has(t.id));

  if (uncategorized.length > 0) {
    const existingOther = categoryEntries.find(c => c.id === 'other');
    if (existingOther) {
      existingOther.tests = [...existingOther.tests, ...uncategorized];
    } else {
      categoryEntries.push({
        id: 'other',
        name: 'Other Available Tools',
        tests: uncategorized,
      });
    }
  }

  return { mseTest: mseTest || null, categories: categoryEntries };
};

const getCategoryEmoji = (catId) => {
  switch (catId) {
    case 'disorders': return '🔎';
    case 'cbt': return '🧠';
    case 'personality': return '👤';
    case 'wellbeing': return '⭐';
    case 'social': return '👥';
    case 'resilience': return '🛡️';
    case 'mood': return '💙';
    case 'development': return '⚖️';
    default: return '📋';
  }
};

const sanitizePhoneForWhatsApp = (phone) => (phone || '').replace(/[^\d]/g, '');

const buildWhatsAppUrl = ({ phone, link, client }) => {
  const normalizedPhone = sanitizePhoneForWhatsApp(phone);
  if (!normalizedPhone) return null;
  const message = encodeURIComponent(`Hello ${client || ''}, here is your psychology assessment link: ${link}`.trim());
  return `https://wa.me/${normalizedPhone}?text=${message}`;
};

const formatDate = (date) => date ? new Date(date).toLocaleDateString() : '—';
const getAssignmentLink = (assignment) => assignment.testLink || assignment.paymentLink || `${CLIENT_URL}/#/test/${assignment.uniqueCode}`;
const getCorporateMemberLink = (assignment) => assignment.memberTestLink || (assignment.corporateMemberCode ? `${CLIENT_URL}/#/test/${assignment.corporateMemberCode}` : '');

const MobileSendTest = () => {
  const [mseTest, setMseTest] = useState(null);
  const [testCategories, setTestCategories] = useState([]);
  const [expandedCats, setExpandedCats] = useState(['disorders']);
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedTests, setSelectedTests] = useState([]);
  const [linkType, setLinkType] = useState('individual');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientWhatsapp, setClientWhatsapp] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [recipientCount, setRecipientCount] = useState(1);
  const [sendEmail, setSendEmail] = useState(true);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadTests();
    loadAssignments();
  }, []);

  const loadTests = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/practitioner/tests`);
      const { mseTest: mse, categories } = categorizeTests(res.data || []);
      setMseTest(mse);
      setTestCategories(categories);
    } catch (error) {
      console.error('Mobile tests load error:', error);
      toast.error('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      setLoadingAssignments(true);
      const res = await axios.get(`${API_URL}/practitioner/assignments?limit=50`);
      setAssignments(res.data.assignments || []);
    } catch (error) {
      console.error('Mobile assignments load error:', error);
    } finally {
      setLoadingAssignments(false);
    }
  };

  const selectedIds = selectedTests.map(test => test.id);
  const selectedNames = selectedTests.map(test => test.shortName || test.name);
  const totalPrice = selectedTests.reduce((sum, test) => sum + (parseFloat(test.price) || 0), 0);
  const hasSelection = selectedIds.length > 0;
  const searchKey = query.trim().toLowerCase();

  const filteredMseTest = !mseTest ? null : (!searchKey
    || mseTest.name.toLowerCase().includes(searchKey)
    || (mseTest.description || '').toLowerCase().includes(searchKey)
    || (mseTest.shortName || '').toLowerCase().includes(searchKey)
      ? mseTest
      : null);

  const filteredCategories = !searchKey
    ? testCategories
    : testCategories
        .map(cat => ({
          ...cat,
          tests: cat.tests.filter(test =>
            test.name.toLowerCase().includes(searchKey)
            || (test.description || '').toLowerCase().includes(searchKey)
            || (test.shortName || '').toLowerCase().includes(searchKey)
          ),
        }))
        .filter(cat => cat.tests.length > 0);

  const totalTests = testCategories.reduce((sum, category) => sum + category.tests.length, 0) + (mseTest ? 1 : 0);

  const toggleCategory = (catId) => setExpandedCats(prev => prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]);

  const toggleTest = (test) => {
    setSuccess(null);
    setSelectedTests(prev => prev.some(item => item.id === test.id)
      ? prev.filter(item => item.id !== test.id)
      : [...prev, test]
    );
  };

  const clearSelection = () => {
    setSelectedTests([]);
    setSuccess(null);
  };

  const copyLink = async (link) => {
    await navigator.clipboard.writeText(link);
    toast.success('Link copied!');
  };

  const openWhatsApp = ({ phone, link, client }) => {
    const whatsappUrl = buildWhatsAppUrl({ phone, link, client });
    if (!whatsappUrl) {
      toast.error('Please enter a WhatsApp number including country code (e.g. 15551234567)');
      return false;
    }
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    return true;
  };

  const createAssignment = async (event) => {
    event.preventDefault();
    if (selectedIds.length === 0) { toast.error('Please select at least one test'); return; }
    if (linkType === 'individual' && !clientName.trim()) { toast.error('Please enter client name'); return; }
    if ((linkType === 'organization' || linkType === 'corporate') && (!organizationName.trim() || Number(recipientCount) < 1)) { toast.error('Please enter organization name and participant count'); return; }
    if (linkType === 'corporate' && Number(recipientCount) < 2) { toast.error('Corporate links require at least 2 paid participants'); return; }

    const whatsappPhone = sanitizePhoneForWhatsApp(clientWhatsapp);
    const payload = { testIds: selectedIds, linkType };
    if (linkType === 'individual') {
      payload.clientName = clientName;
      payload.clientEmail = clientEmail || null;
      payload.clientPhone = whatsappPhone || null;
      payload.sendEmail = sendEmail && Boolean(clientEmail.trim());
    } else {
      payload.organizationName = organizationName;
      payload.recipientCount = Number(recipientCount);
    }

    try {
      setCreating(true);
      const res = await axios.post(`${API_URL}/practitioner/assignments`, payload);
      const createdAssignment = {
        ...res.data.assignment,
        clientPhone: res.data.assignment.clientPhone || whatsappPhone,
      };
      setSuccess(createdAssignment);
      setClientName('');
      setClientEmail('');
      setClientWhatsapp('');
      setOrganizationName('');
      setRecipientCount(1);
      toast.success('Link created successfully!');
      if (linkType === 'individual' && whatsappPhone) {
        openWhatsApp({ phone: whatsappPhone, link: createdAssignment.testLink, client: createdAssignment.clientName || clientName });
      }
      loadAssignments();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create test link');
    } finally {
      setCreating(false);
    }
  };

  return (
    <MobilePage>
      <MobileHero>
        <HeroPill>Assessment Distribution</HeroPill>
        <h2>Send Assessment</h2>
        <p>Create secure links to distribute psychological assessments to individual clients or entire organisations.</p>
      </MobileHero>

      <StepCard>
        <Step $active={!hasSelection} $done={hasSelection}><span>{hasSelection ? '✓' : '1'}</span><strong>Select Assessment</strong></Step>
        <StepLine $active={hasSelection} />
        <Step $active={hasSelection && !success} $done={Boolean(success)}><span>{success ? '✓' : '2'}</span><strong>Create Link</strong></Step>
      </StepCard>

      {loading ? (
        <>
          <Skeleton $height="210px" />
          <Skeleton $height="300px" />
        </>
      ) : (
        <>
          <Card>
            <SectionHeader>
              <div><span>Assessment catalog</span><h3>{totalTests} available tools</h3></div>
              <Badge>₵{totalPrice.toFixed(2)}</Badge>
            </SectionHeader>
            <Field>
              Search assessment catalog
              <Input value={query} onChange={event => setQuery(event.target.value)} placeholder="MSE, anxiety, depression…" />
            </Field>

            {selectedTests.length > 0 && (
              <SelectionPanel>
                <div>
                  <strong>{selectedTests.length} selected</strong>
                  <span>{selectedNames.join(', ')}</span>
                </div>
                <button type="button" onClick={clearSelection}>Clear</button>
              </SelectionPanel>
            )}

            {filteredMseTest && (
              <MseBlock>
                <TestButton type="button" onClick={() => toggleTest(filteredMseTest)} $selected={selectedIds.includes(filteredMseTest.id)}>
                  <div>
                    <strong>🧠 {filteredMseTest.shortName || filteredMseTest.name}</strong>
                    <span>{filteredMseTest.description || filteredMseTest.name} • ₵{parseFloat(filteredMseTest.price || 0).toFixed(2)}</span>
                  </div>
                  <Badge $tone={selectedIds.includes(filteredMseTest.id) ? 'paid' : 'completed'}>{selectedIds.includes(filteredMseTest.id) ? 'Added' : 'Add'}</Badge>
                </TestButton>
              </MseBlock>
            )}

            <CategoryList>
              {filteredCategories.map(category => (
                <CategoryBlock key={category.id}>
                  <CategoryHead type="button" onClick={() => toggleCategory(category.id)}>
                    <span>{getCategoryEmoji(category.id)}</span>
                    <div><strong>{category.name}</strong><small>{category.tests.length} test{category.tests.length === 1 ? '' : 's'}</small></div>
                    <Chevron $open={expandedCats.includes(category.id)}>⌄</Chevron>
                  </CategoryHead>
                  {expandedCats.includes(category.id) && (
                    <CategoryBody>
                      {category.tests.map(test => {
                        const selected = selectedIds.includes(test.id);
                        return (
                          <TestButton key={test.id} type="button" onClick={() => toggleTest(test)} $selected={selected}>
                            <div>
                              <strong>{test.shortName || test.name}</strong>
                              <span>{test.description || test.name} • ₵{parseFloat(test.price || 0).toFixed(2)}</span>
                            </div>
                            <Badge $tone={selected ? 'paid' : 'completed'}>{selected ? 'Added' : 'Add'}</Badge>
                          </TestButton>
                        );
                      })}
                    </CategoryBody>
                  )}
                </CategoryBlock>
              ))}
            </CategoryList>
          </Card>

          <Card as="form" onSubmit={createAssignment}>
            <SectionHeader><div><span>Create link</span><h3>Recipient details</h3></div></SectionHeader>
            <Field>
              Link type
              <Select value={linkType} onChange={event => setLinkType(event.target.value)}>
                <option value="individual">Individual client</option>
                <option value="organization">Organisation / group (each pays)</option>
                <option value="corporate">Corporate (manager prepays)</option>
              </Select>
            </Field>
            {linkType === 'individual' ? (
              <FormStack>
                <Field>Client name <Input value={clientName} onChange={event => setClientName(event.target.value)} placeholder="Client full name" /></Field>
                <Field>Email address <Input value={clientEmail} onChange={event => setClientEmail(event.target.value)} placeholder="client@example.com" type="email" /></Field>
                <Field>WhatsApp number <Input value={clientWhatsapp} onChange={event => setClientWhatsapp(event.target.value)} placeholder="233…" inputMode="tel" /></Field>
                <ToggleLabel><input checked={sendEmail} onChange={event => setSendEmail(event.target.checked)} type="checkbox" /> Send email if address is provided</ToggleLabel>
              </FormStack>
            ) : (
              <FormStack>
                <Field>Organisation name <Input value={organizationName} onChange={event => setOrganizationName(event.target.value)} placeholder="Organisation" /></Field>
                <Field>{linkType === 'corporate' ? 'Paid participants' : 'Expected recipients'} <Input value={recipientCount} min={linkType === 'corporate' ? '2' : '1'} onChange={event => setRecipientCount(event.target.value)} type="number" /></Field>
                {linkType === 'corporate' && <small>Corporate total: ₵{(totalPrice * (Number(recipientCount) || 0)).toFixed(2)}. Unique participant email required; no cross-browser retakes.</small>}
              </FormStack>
            )}
            <Button $full disabled={creating || selectedIds.length === 0} type="submit">{creating ? 'Creating…' : 'Generate Link'}</Button>
          </Card>

          {success && (
            <Card>
              <SectionHeader><div><span>Ready to share</span><h3>Link Created!</h3></div></SectionHeader>
              <SuccessSub>{success.testsCount > 1 ? `${success.testsCount} assessments ready` : `Ready for ${success.clientName || success.organizationName || 'recipient'}`}</SuccessSub>
              {(success.linkType === 'corporate' || success.requiresPractitionerAssessmentLink) ? (
                <>
                  <small>{success.linkType === 'corporate' ? 'Manager payment link' : 'Payment link'}</small>
                  <LinkBox>{success.paymentLink || success.testLink}</LinkBox>
                  <small>{success.linkType === 'corporate' ? 'Member assessment link' : 'Open assessment link'}</small>
                  <LinkBox>{success.memberTestLink}</LinkBox>
                </>
              ) : <LinkBox>{success.testLink}</LinkBox>}
              <ActionRow>
                {(success.linkType === 'corporate' || success.requiresPractitionerAssessmentLink) ? (
                  <>
                    <Button type="button" onClick={() => copyLink(success.paymentLink || success.testLink)}>Copy Payment</Button>
                    <Button $secondary type="button" onClick={() => copyLink(success.memberTestLink)}>{success.linkType === 'corporate' ? 'Copy Member' : 'Open Assessment Link'}</Button>
                  </>
                ) : <Button type="button" onClick={() => copyLink(success.testLink)}>Copy Link</Button>}
                {linkType === 'individual' && (
                  <Button $secondary type="button" onClick={() => openWhatsApp({ phone: success.clientPhone || clientWhatsapp, link: success.testLink, client: success.clientName || clientName })}>WhatsApp</Button>
                )}
              </ActionRow>
              <MetaChips>
                {success.uniqueCode && <Badge>Code: {success.uniqueCode}</Badge>}
                {success.expiresAt && <Badge $tone="pending">Expires: {formatDate(success.expiresAt)}</Badge>}
              </MetaChips>
            </Card>
          )}

          {(loadingAssignments || assignments.length > 0) && (
            <Card>
              <SectionHeader><div><span>Recent links</span><h3>Corporate Link History</h3></div></SectionHeader>
              {loadingAssignments ? <EmptyState><strong>Loading your test links…</strong><p>Please wait while we fetch recent assignments.</p></EmptyState> : (
                <RecentTable>
                  {assignments.filter(assignment => assignment.linkType === 'corporate').slice(0, 15).map(assignment => {
                    const paymentLink = getAssignmentLink(assignment);
                    const memberLink = getCorporateMemberLink(assignment);
                    return (
                      <RecentRow key={assignment.id}>
                        <RecentTop>
                          <div>
                            <strong>{assignment.clientName || assignment.organizationName || 'Assessment link'}</strong>
                            {assignment.clientEmail && <span>{assignment.clientEmail}</span>}
                            {assignment.recipientCount && <span>{assignment.recipientCount} participant{assignment.recipientCount !== 1 ? 's' : ''}</span>}
                          </div>
                          <Badge $tone={assignment.status === 'completed' ? 'paid' : 'pending'}>{assignment.status || 'active'}</Badge>
                        </RecentTop>
                        <RecentGrid>
                          <div><small>Test</small><b>{assignment.TestCatalog?.shortName || assignment.TestCatalog?.name || assignment.testNames || 'Assessment'}</b></div>
                          <div><small>Payment code</small><code>{assignment.uniqueCode}</code></div>
                          <div><small>Member code</small><code>{assignment.corporateMemberCode || '—'}</code></div>
                          <div><small>Created</small><b>{formatDate(assignment.createdAt)}</b></div>
                        </RecentGrid>
                        <ActionRow>
                          <Button type="button" onClick={() => copyLink(paymentLink)}>Copy Payment</Button>
                          {memberLink && <Button $secondary type="button" onClick={() => copyLink(memberLink)}>Copy Member</Button>}
                          {assignment.clientPhone && <Button $secondary type="button" onClick={() => openWhatsApp({ phone: assignment.clientPhone, link: paymentLink, client: assignment.clientName })}>WhatsApp</Button>}
                        </ActionRow>
                      </RecentRow>
                    );
                  })}
                </RecentTable>
              )}
            </Card>
          )}
        </>
      )}
    </MobilePage>
  );
};

const StepCard = styled.div`
  align-items: center;
  background: white;
  border: 1px solid #e8f0fe;
  border-radius: 22px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
  display: grid;
  grid-template-columns: 1fr 44px 1fr;
  padding: 12px;
`;

const Step = styled.div`
  align-items: center;
  color: ${p => (p.$done ? '#059669' : p.$active ? '#075985' : '#94a3b8')};
  display: flex;
  flex-direction: column;
  font-size: 0.72rem;
  font-weight: 900;
  gap: 6px;
  text-align: center;

  span {
    align-items: center;
    background: ${p => (p.$done ? '#dcfce7' : p.$active ? '#e0f2fe' : '#f1f5f9')};
    border-radius: 50%;
    display: flex;
    height: 30px;
    justify-content: center;
    width: 30px;
  }
`;

const StepLine = styled.div`
  background: ${p => (p.$active ? '#93c5fd' : '#e2e8f0')};
  height: 3px;
`;

const SelectionPanel = styled.div`
  align-items: center;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 18px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-top: 12px;
  padding: 12px;

  strong { color: #075985; display: block; font-size: 0.86rem; }
  span { color: #0369a1; display: block; font-size: 0.74rem; margin-top: 3px; }
  button { background: transparent; border: 0; color: #dc2626; font-weight: 900; }
`;

const MseBlock = styled.div`
  margin-top: 12px;
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

const CategoryBlock = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  overflow: hidden;
`;

const CategoryHead = styled.button`
  align-items: center;
  background: white;
  border: 0;
  color: #0f172a;
  display: flex;
  gap: 11px;
  padding: 13px;
  text-align: left;
  width: 100%;

  > span {
    align-items: center;
    background: #e0f2fe;
    border-radius: 15px;
    display: flex;
    height: 40px;
    justify-content: center;
    width: 40px;
  }

  div { flex: 1; min-width: 0; }
  strong { display: block; font-size: 0.88rem; }
  small { color: #64748b; display: block; font-size: 0.72rem; margin-top: 3px; }
`;

const Chevron = styled.i`
  color: #64748b;
  font-style: normal;
  transform: rotate(${p => (p.$open ? '180deg' : '0deg')});
`;

const CategoryBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
`;

const TestButton = styled.button`
  align-items: center;
  background: ${p => (p.$selected ? '#eff6ff' : '#ffffff')};
  border: 1px solid ${p => (p.$selected ? '#93c5fd' : '#e2e8f0')};
  border-radius: 18px;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  min-height: 68px;
  padding: 12px;
  text-align: left;
  width: 100%;

  div { flex: 1; min-width: 0; }
  strong { color: #0f172a; display: block; font-size: 0.9rem; }
  span { color: #64748b; display: -webkit-box; font-size: 0.74rem; line-height: 1.35; margin-top: 4px; overflow: hidden; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
`;

const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 12px 0;
`;

const ToggleLabel = styled.label`
  align-items: center;
  color: #475569;
  display: flex;
  font-size: 0.8rem;
  font-weight: 800;
  gap: 8px;
`;

const SuccessSub = styled.p`
  color: #64748b;
  font-size: 0.82rem;
  margin: -4px 0 12px;
`;

const LinkBox = styled.div`
  background: #f8fafc;
  border: 1px dashed #bfdbfe;
  border-radius: 16px;
  color: #075985;
  font-size: 0.82rem;
  margin-bottom: 12px;
  overflow-wrap: anywhere;
  padding: 12px;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;

  button { flex: 1; }
`;

const MetaChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const RecentTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RecentRow = styled.article`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 12px;
`;

const RecentTop = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 10px;
  justify-content: space-between;

  strong { color: #0f172a; display: block; font-size: 0.9rem; }
  span { color: #64748b; display: block; font-size: 0.74rem; margin-top: 3px; }
`;

const RecentGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 12px;

  div { background: white; border-radius: 14px; padding: 9px; }
  small { color: #94a3b8; display: block; font-size: 0.65rem; font-weight: 900; text-transform: uppercase; }
  b, code { color: #0f172a; display: block; font-size: 0.72rem; margin-top: 4px; overflow-wrap: anywhere; }
`;

export default MobileSendTest;
